import { PropsWithChildren, createContext, useContext, useMemo } from 'react';

import { DEFAULT_THEME, SUPPORTED_THEMES, Theme, ThemeClassName, useTheme } from '../hooks';

/**
 * THEME CONTEXT CREATION - Create context with default values
 * 
 * This creates the React Context that will hold theme information.
 * Default values are used as fallback if Provider is missing (shouldn't happen).
 */
const ThemeContext = createContext<{
  theme: Theme;
  themeClassName: ThemeClassName;
}>({
  theme: DEFAULT_THEME,
  themeClassName: SUPPORTED_THEMES[DEFAULT_THEME],
});

/**
 * This hook simplifies consuming the ThemeContext in any component.
 * 
 * RETURNS:
 * - theme: Current theme name for logic (e.g., if (theme === 'dark') {...})
 * - themeClassName: CSS class for styling (e.g., <div className={themeClassName}>)
 */
export const useThemeContext = () => useContext(ThemeContext);

/**
 * THEME CONTEXT PROVIDER - Wraps app to provide theme to all children
 * 
 * This component must wrap the entire app (usually in BootComponent) to make
 * theme accessible to all child components via useThemeContext() hook.
 */
export const ThemeContextProvider = ({
  targetOrigin,themeValue,
  children,
}: PropsWithChildren<{ targetOrigin: string, themeValue: string }>) => {
  
  /**
   * Get current theme using useTheme hook
   * This hook handles:
   * - Reading theme from themeValue prop (forced by native/parent)
   * - Reading from localStorage (user's previous selection)
   * - Reading from system preferences (prefers-color-scheme)
   * - Listening for theme changes from parent window/native app
   */
  const theme = useTheme(targetOrigin, themeValue);

  /**
   * Memoize context value for performance
   * - Context value is an object, which is recreated on every render
   * - Without memo, ALL consumers re-render on EVERY provider render
   * - With memo, consumers only re-render when theme actually changes
   * 
   * DEPENDENCIES: [theme]
   * - Only recreate value when theme changes
   * - themeClassName is derived from theme, so it updates too
   */
  const value = useMemo(
    () => ({
      theme,
      themeClassName: SUPPORTED_THEMES[theme],
    }),
    [theme],
  );

  /** Provide theme value to all children, Any child component can access theme via useThemeContext() hook */
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
